/**
 * Plan Definitions & Limit Checking Utility
 * Dynamic Pricing Engine — plans come from API/sync, these are fallback helpers.
 */

/** Fallback plan definitions (used before API data loads) */
export const PLANS = {
  free: { maxProperties: 2, label: "Free" },
  starter: { maxProperties: 10, label: "Starter" },
  growth: { maxProperties: 999999, label: "Growth" },
  pro: { maxProperties: 999999, label: "Pro" }
};

/**
 * Check if user can add a new property.
 * @param {string} planType - plan id
 * @param {number} currentCount - Current property count
 * @param {number} [serverLimit] - Server-provided limit (overrides default)
 * @returns {boolean}
 */
export function canAddProperty(planType, currentCount, serverLimit) {
  const limit = serverLimit ?? PLANS[planType]?.maxProperties ?? 2;
  return currentCount < limit;
}

/**
 * Check if a feature is available on the current plan.
 * Uses the features array from sync data if available.
 * @param {string} featureKey - e.g. "leases", "whatsapp", "analytics"
 * @param {string[]} [planFeatures] - features array from plan data
 * @returns {boolean}
 */
export function hasFeature(featureKey, planFeatures) {
  if (!planFeatures || !Array.isArray(planFeatures)) return false;
  return planFeatures.includes(featureKey);
}

/**
 * Check if a feature is pro-only (for badge display).
 * Uses features array — if the feature isn't in the current plan's list, it's "locked".
 * @param {string} featureKey
 * @param {string[]} [planFeatures] - current plan's features
 * @returns {boolean}
 */
export function isProFeature(featureKey, planFeatures) {
  if (!planFeatures || !Array.isArray(planFeatures)) {
    // Fallback: these features are typically not in free plan
    return ["leases", "mapView", "fitInsights", "whatsapp", "csvExport", "analytics", "automation", "service_persons"].includes(featureKey);
  }
  return !planFeatures.includes(featureKey);
}

/**
 * Get plan usage info for display.
 * @param {string} planType - plan id
 * @param {number} currentCount - Current property count
 * @param {number} [serverLimit] - Server-provided limit
 * @returns {{ used: number, limit: number, pct: number, atLimit: boolean, label: string }}
 */
export function getPlanUsage(planType, currentCount, serverLimit) {
  const plan = PLANS[planType] || PLANS.free;
  const limit = serverLimit ?? plan.maxProperties;
  const isUnlimited = limit >= 999999;
  return {
    used: currentCount,
    limit: isUnlimited ? Infinity : limit,
    pct: isUnlimited ? 0 : (limit > 0 ? Math.min(100, Math.round((currentCount / limit) * 100)) : 100),
    atLimit: !isUnlimited && currentCount >= limit,
    label: plan.label
  };
}

/**
 * Format price from paise to display string.
 * @param {number} paise
 * @returns {string}
 */
export function formatPrice(paise) {
  if (!paise || paise <= 0) return "\u20B90";
  return `\u20B9${Math.round(paise / 100)}`;
}
